import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import APIService from "../APIService";
import FacultyDashboardShell from "./FacultyDashboardShell";
import { formatLocalDateTime } from "../utils/datetime";
import { getFacultyWorkspaceCourse, setFacultyWorkspaceCourse } from "../utils/facultyWorkspace";
import ChatMessage from "./ChatMessage";
import "./Dashboard.css";

function SessionReviewPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [courseFilter, setCourseFilter] = useState(() => getFacultyWorkspaceCourse()?.courseId || "");
  const [assignmentFilter, setAssignmentFilter] = useState("");
  const [studentFilter, setStudentFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    let active = true;

    async function bootstrap() {
      try {
        const data = await APIService.GetUserPrimaryAttribute();
        const isManager = Boolean(data?.faculty_access);

        if (!isManager) {
          navigate("/", { replace: true });
          return;
        }

        if (active) {
          setAuthorized(true);
        }
      } catch (_error) {
        navigate("/", { replace: true });
      } finally {
        if (active) {
          setCheckingAuth(false);
        }
      }
    }

    bootstrap();
    return () => {
      active = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    let active = true;

    async function loadSessions() {
      try {
        setLoading(true);
        const data = await APIService.GetFacultyInsights({
          course: courseFilter,
          assignment: assignmentFilter,
          student: studentFilter,
          dateFrom,
          dateTo,
        });
        if (!active) return;
        setPayload(data);
        setError("");
      } catch (loadError) {
        if (!active) return;
        if (loadError?.status === 401 || loadError?.status === 403) {
          navigate("/", { replace: true });
          return;
        }
        setError(loadError.message || "Failed to load tutoring sessions.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadSessions();
    return () => {
      active = false;
    };
  }, [authorized, assignmentFilter, courseFilter, dateFrom, dateTo, navigate, studentFilter]);

  useEffect(() => {
    if (!authorized) {
      return;
    }

    const handleVisibilityRefresh = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      setLoading(true);
      APIService.GetFacultyInsights({
        course: courseFilter,
        assignment: assignmentFilter,
        student: studentFilter,
        dateFrom,
        dateTo,
      })
        .then((data) => {
          setPayload(data);
          setError("");
        })
        .catch((loadError) => {
          if (loadError?.status === 401 || loadError?.status === 403) {
            navigate("/", { replace: true });
            return;
          }
          setError(loadError.message || "Failed to load tutoring sessions.");
        })
        .finally(() => setLoading(false));
    };

    window.addEventListener("focus", handleVisibilityRefresh);
    document.addEventListener("visibilitychange", handleVisibilityRefresh);
    return () => {
      window.removeEventListener("focus", handleVisibilityRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityRefresh);
    };
  }, [authorized, assignmentFilter, courseFilter, dateFrom, dateTo, navigate, studentFilter]);

  const sessions = useMemo(() => payload?.sessions || [], [payload]);
  const filteredSessions = useMemo(() => {
    const searchValue = searchText.trim().toLowerCase();
    if (!searchValue) {
      return sessions;
    }

    return sessions.filter((session) => {
      const haystack = [
        session.studentDisplayName,
        session.studentUniqueId,
        session.courseCovered,
        session.assignmentName,
        session.latestQuestion,
        (session.questionCategories || []).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(searchValue);
    });
  }, [searchText, sessions]);

  const selectedSession = useMemo(
    () => filteredSessions.find((session) => session.sessionId === selectedSessionId) || null,
    [filteredSessions, selectedSessionId]
  );

  const studentOptions = useMemo(() => {
    const map = new Map();
    sessions.forEach((session) => {
      if (!session.studentUniqueId) return;
      map.set(session.studentUniqueId, session.studentDisplayName || session.studentUniqueId);
    });
    return Array.from(map.entries()).sort((left, right) => left[1].localeCompare(right[1]));
  }, [sessions]);

  const courseOptions = [...new Set(sessions.map((session) => session.courseCovered).filter(Boolean))];
  const assignmentOptions = [...new Set(
    sessions
      .filter((session) => !courseFilter || session.courseCovered === courseFilter)
      .map((session) => session.assignmentName)
      .filter(Boolean)
  )];

  const workspaceCourse = useMemo(() => {
    if (!courseFilter) {
      return getFacultyWorkspaceCourse();
    }
    return {
      courseId: courseFilter,
      name: "",
    };
  }, [courseFilter]);

  useEffect(() => {
    setFacultyWorkspaceCourse(workspaceCourse);
  }, [workspaceCourse]);

  useEffect(() => {
    if (!selectedSessionId && filteredSessions.length > 0) {
      setSelectedSessionId(filteredSessions[0].sessionId);
    }
    if (selectedSessionId && !filteredSessions.some((session) => session.sessionId === selectedSessionId)) {
      setSelectedSessionId(filteredSessions[0]?.sessionId || null);
      setMessages([]);
    }
  }, [filteredSessions, selectedSessionId]);

  useEffect(() => {
    if (!selectedSessionId) {
      setMessages([]);
      return;
    }

    if (selectedSession && selectedSession.isTranscriptBacked === false) {
      setMessages([]);
      setLoadingMessages(false);
      return;
    }

    let active = true;

    async function loadMessages() {
      try {
        setLoadingMessages(true);
        setMessages([]);
        const nextMessages = await APIService.GetFacultySessionMessages(selectedSessionId);
        if (!active) return;
        setMessages(nextMessages);
      } catch (loadError) {
        if (!active) return;
        if (loadError?.status === 401 || loadError?.status === 403) {
          navigate("/", { replace: true });
          return;
        }
        setError(loadError.message || "Failed to load session transcript.");
      } finally {
        if (active) {
          setLoadingMessages(false);
        }
      }
    }

    loadMessages();
    return () => {
      active = false;
    };
  }, [navigate, selectedSession, selectedSessionId]);

  if (checkingAuth || !authorized) {
    return null;
  }

  return (
    <FacultyDashboardShell
      title="Sessions"
      description="Review student conversations and open individual transcripts."
      error={error}
      loading={loading}
      loadingLabel="Loading session review…"
      workspaceCourse={workspaceCourse}
      workspaceMode={courseFilter ? "selected_course" : "all_courses"}
      workspaceHint={
        courseFilter
          ? "This section is filtered to the selected course so transcript review stays focused."
          : "Use the filters to review tutoring sessions across your visible courses."
      }
      nextActions={
        courseFilter
          ? [
              { to: "/dashboard", label: "My Courses" },
              { to: "/faculty/uploads", label: "Content" },
              { to: "/faculty/insights", label: "Insights" },
            ]
          : [{ to: "/dashboard", label: "My Courses" }]
      }
    >
      {!loading && !error && (
        <>
          <main id="main-content" className="session-review-workspace">
            <aside className="faculty-sidebar-pane session-review-sidebar" aria-label="Session review filters and list">
              <div className="faculty-card">
                <div className="faculty-pane-header compact">
                  <div>
                    <div className="faculty-eyebrow">Filters</div>
                    <h2>Find sessions</h2>
                  </div>
                </div>
                <div className="faculty-filter-grid session-review-filter-grid">
                  <div className="session-filter-field">
                    <label className="session-filter-label" htmlFor="session-search">
                      Search sessions
                    </label>
                    <input
                      id="session-search"
                      className="history-input"
                      type="text"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder="Search by student, course, assignment, or keyword"
                      aria-label="Search sessions"
                    />
                  </div>

                  <div className="session-filter-field">
                    <label className="session-filter-label" htmlFor="session-student-filter">
                      Student
                    </label>
                    <select
                      id="session-student-filter"
                      className="history-input"
                      value={studentFilter}
                      onChange={(event) => setStudentFilter(event.target.value)}
                      aria-label="Filter sessions by student"
                    >
                      <option value="">All students</option>
                      {studentOptions.map(([studentId, label]) => (
                        <option key={studentId} value={studentId}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="session-filter-field">
                    <label className="session-filter-label" htmlFor="session-course-filter">
                      Course
                    </label>
                    <select
                      id="session-course-filter"
                      className="history-input"
                      value={courseFilter}
                      onChange={(event) => { setCourseFilter(event.target.value); setAssignmentFilter(""); }}
                      aria-label="Filter sessions by course"
                    >
                      <option value="">All courses</option>
                      {courseOptions.map((course) => (
                        <option key={course} value={course}>{course}</option>
                      ))}
                    </select>
                  </div>

                  <div className="session-filter-field">
                    <label className="session-filter-label" htmlFor="session-assignment-filter">
                      Assignment or topic
                    </label>
                    <select
                      id="session-assignment-filter"
                      className="history-input"
                      value={assignmentFilter}
                      onChange={(event) => setAssignmentFilter(event.target.value)}
                      aria-label="Filter sessions by assignment or topic"
                    >
                      <option value="">All assignments / topics</option>
                      {assignmentOptions.map((assignment) => (
                        <option key={assignment} value={assignment}>{assignment}</option>
                      ))}
                    </select>
                  </div>

                  <div className="session-filter-field session-filter-date-group">
                    <div className="session-filter-label">Session start date</div>
                    <div className="session-filter-date-grid">
                      <div className="session-filter-field compact">
                        <label className="session-filter-sublabel" htmlFor="session-date-from">
                          Start date
                        </label>
                        <input
                          id="session-date-from"
                          className="history-input"
                          type="date"
                          value={dateFrom}
                          onChange={(event) => setDateFrom(event.target.value)}
                          aria-label="Filter sessions by session start date from"
                        />
                      </div>
                      <div className="session-filter-field compact">
                        <label className="session-filter-sublabel" htmlFor="session-date-to">
                          End date
                        </label>
                        <input
                          id="session-date-to"
                          className="history-input"
                          type="date"
                          value={dateTo}
                          onChange={(event) => setDateTo(event.target.value)}
                          aria-label="Filter sessions by session start date to"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="faculty-card session-review-list-card">
                <div className="faculty-pane-header compact">
                  <div>
                    <div className="faculty-eyebrow">Sessions</div>
                    <h2>{filteredSessions.length} matching sessions</h2>
                  </div>
                </div>

                <div className="session-review-list" role="list" aria-label="Filtered tutoring sessions">
                  {filteredSessions.map((session) => (
                    <button
                      key={session.sessionId}
                      type="button"
                      className={`session-review-item ${selectedSessionId === session.sessionId ? "active" : ""}`}
                      onClick={() => setSelectedSessionId(session.sessionId)}
                    >
                      <div className="session-review-item-header">
                        <strong>{session.studentDisplayName || session.studentUniqueId}</strong>
                        <span>{formatLocalDateTime(session.endTime || session.startTime)}</span>
                      </div>
                      <span>{session.courseCovered}</span>
                      <span>{session.assignmentName || "Course-wide context"}</span>
                      <div className="session-review-item-meta">
                        <span>{session.messageCount || 0} messages</span>
                        <span>View transcript</span>
                      </div>
                      <p>{session.latestQuestion || "No question preview available."}</p>
                    </button>
                  ))}
                  {filteredSessions.length === 0 && (
                    <div className="faculty-empty-state">
                      No sessions match the current filters yet. Try broadening the session start date range or clearing one of the filters.
                    </div>
                  )}
                </div>
              </div>
            </aside>

            <section className="faculty-detail-pane session-review-transcript" aria-label="Session transcript viewer">
              <div className="session-review-transcript-topbar">
                <div>
                  <div className="faculty-eyebrow">Transcript</div>
                  <h2>{selectedSession ? (selectedSession.studentDisplayName || selectedSession.studentUniqueId) : "Choose a session"}</h2>
                  <p>
                    {selectedSession
                      ? `${selectedSession.courseCovered} · ${selectedSession.assignmentName || "Course-wide context"}`
                      : "Select a session to inspect the tutoring exchange."}
                  </p>
                </div>
                {selectedSession && (
                  <div className="faculty-upload-context">
                    <span className="faculty-status-pill">{selectedSession.messageCount || 0} messages</span>
                    <span className="faculty-status-pill">{selectedSession.durationMinutes ?? "—"} min</span>
                    <span className={`faculty-status-pill ${selectedSession.isTranscriptBacked ? "active" : "archived"}`}>
                      {selectedSession.transcriptStatus || "unknown"}
                    </span>
                  </div>
                )}
              </div>

              {selectedSession && (
                <div className="session-review-metadata">
                  <span>{selectedSession.studentDisplayName || selectedSession.studentUniqueId}</span>
                  <span>{formatLocalDateTime(selectedSession.startTime)}</span>
                  <span>{selectedSession.questionCategories?.join(", ") || "Uncategorized"}</span>
                </div>
              )}

              <div className="session-review-messages" role="log" aria-live="polite">
                {!selectedSession && (
                  <div className="faculty-empty-state">Select a session from the left to review the tutoring transcript.</div>
                )}
                {selectedSession && loadingMessages && (
                  <div className="faculty-empty-state" role="status">Loading transcript details…</div>
                )}
                {selectedSession && !loadingMessages && messages.length === 0 && (
                  <div className="faculty-empty-state">No transcript messages were found for this session.</div>
                )}
                {messages.map((message, index) => (
                  <ChatMessage
                    key={`${message.createdAt || "message"}-${index}`}
                    role={message.type === "user" ? "user" : "assistant"}
                    content={message.text}
                    label={message.type === "user" ? "Student" : "AI Tutor"}
                  />
                ))}
              </div>
            </section>
          </main>
        </>
      )}
    </FacultyDashboardShell>
  );
}

export default SessionReviewPage;
